import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtEUR } from "../lib/api";
import { primaryImg } from "../lib/img";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { StarRatingDisplay } from "../components/StarRating";
import SEO from "../components/SEO";

export default function Shop() {
  const [products, setProducts] = useState([]);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    api.get("/products").then(r => setProducts(r.data)).catch(() => {});
  }, []);

  const filtered = products.filter(p => {
    if (filter === "all") return true;
    if (filter === "promo") return p.is_promo;
    if (filter === "limited") return p.is_limited;
    return p.category === filter;
  });

  const categories = Array.from(new Set(products.map(p => p.category)));

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-16" data-testid="shop-page">
      <SEO pageSlug="shop" title="Boutique" description="Tous les produits Herbalife — nutrition, boissons et compléments. Livraison Luxembourg, France, Belgique." path="/shop" />
      <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
        <div>
          <div className="text-xs font-bold tracking-[0.3em] uppercase text-[#7AC143] mb-3">Boutique</div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-[#1A2E1A]">Tous nos produits</h1>
          <p className="text-[#4B634B] mt-3 max-w-xl">Une sélection rigoureuse pour soutenir votre énergie au quotidien.</p>
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-[200px] rounded-full border-[#E5EAE5]" data-testid="shop-filter">
            <SelectValue placeholder="Filtrer" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tout</SelectItem>
            <SelectItem value="promo">En promotion</SelectItem>
            <SelectItem value="limited">Édition limitée</SelectItem>
            {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {filtered.map((p) => {
          const oos = (p.stock ?? 0) <= 0;
          return (
          <Link key={p.id} to={`/shop/${p.id}`} data-testid={`product-card-${p.id}`} className={`product-card group bg-white border border-[#E5EAE5] rounded-2xl overflow-hidden ${oos ? "opacity-75" : ""}`}>
            <div className="aspect-square overflow-hidden bg-[#F2F5F0] relative">
              <img src={primaryImg(p)} alt={p.name} className={`zoom-img w-full h-full object-cover ${oos ? "grayscale" : ""}`} />
              {oos && (
                <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                  <Badge className="bg-red-500 text-white">Rupture de stock</Badge>
                </div>
              )}
            </div>
            <div className="p-5">
              <div className="flex gap-2 mb-2 min-h-[24px] flex-wrap">
                {p.is_limited && <Badge className="bg-[#FACC15] text-[#1A2E1A] hover:bg-[#FACC15] rounded-full">Édition limitée</Badge>}
                {p.is_promo && <Badge className="bg-[#7AC143] hover:bg-[#7AC143] rounded-full">Promo</Badge>}
                {!oos && p.stock < 5 && <Badge className="bg-[#FACC15] text-[#1A2E1A] rounded-full">Plus que {p.stock}</Badge>}
              </div>
              <h3 className="font-bold text-lg leading-tight line-clamp-2">{p.name}</h3>
              {(p.rating_count || 0) > 0 && (
                <div className="mt-1 flex items-center gap-1.5 text-xs text-[#4B634B]" data-testid={`shop-card-rating-${p.id}`}>
                  <StarRatingDisplay value={p.rating_avg} size={13} />
                  <span className="font-semibold text-[#1A2E1A]">{Number(p.rating_avg).toFixed(1)}</span>
                  <span>({p.rating_count})</span>
                </div>
              )}
              <p className="text-sm text-[#4B634B] mt-2 line-clamp-2">{(p.description || "").replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim()}</p>
              <div className="mt-4 flex items-baseline gap-2">
                {p.is_promo && p.promo_price ? (
                  <>
                    <span className="text-[#7AC143] font-black text-xl">{fmtEUR(p.promo_price)}</span>
                    <span className="line-through text-sm text-[#4B634B]">{fmtEUR(p.price)}</span>
                  </>
                ) : (
                  <span className="text-[#1A2E1A] font-black text-xl">{fmtEUR(p.price)}</span>
                )}
              </div>
            </div>
          </Link>
          );
        })}
        {filtered.length === 0 && <p className="text-[#4B634B] col-span-full text-center py-20">Aucun produit.</p>}
      </div>
    </div>
  );
}
